import Button from "@/components/base/Button";
import SectionHeading from "@/components/base/SectionHeading";
import ContactForm from "@/pages/contact/components/ContactForm";
import { clinicInfo } from "@/mocks/site";

export function AppointmentCta() {
  return (
    <section className="relative overflow-hidden">
      {/* Multi-colour gradient banner */}
      <div
        className="absolute inset-0 bg-gradient-to-br from-primary-600 via-secondary-600 to-accent-600"
        aria-hidden="true"
      ></div>
      <div className="absolute -left-16 -top-16 h-72 w-72 rounded-full bg-background-50/15 blur-3xl" aria-hidden="true"></div>
      <div className="absolute -bottom-20 right-0 h-80 w-80 rounded-full bg-accent-400/25 blur-3xl" aria-hidden="true"></div>
      <div className="absolute right-1/4 top-8 h-40 w-40 rounded-full border border-background-50/20" aria-hidden="true"></div>

      <div className="relative mx-auto flex max-w-4xl flex-col items-center px-4 py-16 text-center md:px-6 md:py-24">
        <span className="inline-flex items-center gap-2 rounded-full border border-background-50/30 bg-background-50/10 px-4 py-1.5 font-label text-xs font-semibold uppercase tracking-[0.16em] text-background-50 backdrop-blur">
          <span className="h-1.5 w-1.5 rounded-full bg-secondary-100" aria-hidden="true"></span>
          Book an Appointment
        </span>
        <h2 className="mt-6 font-heading text-3xl font-semibold leading-tight text-background-50 md:text-5xl">
          Ready to take the next step?
        </h2>
        <p className="mt-5 max-w-2xl font-body text-sm leading-relaxed text-background-50/90 md:text-lg">
          Let&rsquo;s understand your condition and explore the right care for you — from a clear diagnosis to a
          treatment plan that puts you first.
        </p>
        <div className="mt-9 flex flex-col gap-3 sm:flex-row">
          <Button to="/appointment" variant="light" size="lg" icon="ri-calendar-check-line">
            Book an Appointment
          </Button>
          <a
            href={clinicInfo.phoneHref}
            className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/50 px-6 py-3 font-label text-base font-semibold text-background-50 transition-colors hover:bg-background-50/15"
          >
            <i className="ri-phone-fill" aria-hidden="true"></i>
            Call {clinicInfo.phoneDisplay}
          </a>
        </div>
      </div>
    </section>
  );
}

export function Contact() {
  interface ContactItem {
    icon: string;
    label: string;
    value: string;
    href?: string;
    bubble: string;
  }

  const contactItems: ContactItem[] = [
    {
      icon: "ri-phone-fill",
      label: "Phone",
      value: clinicInfo.phoneDisplay,
      href: clinicInfo.phoneHref,
      bubble: "bg-primary-500/20 text-primary-200",
    },
    ...(clinicInfo.email
      ? [
          {
            icon: "ri-mail-line",
            label: "Email",
            value: clinicInfo.email,
            href: `mailto:${clinicInfo.email}`,
            bubble: "bg-secondary-500/20 text-secondary-200",
          },
        ]
      : []),
    {
      icon: "ri-map-pin-line",
      label: "Address",
      value: clinicInfo.addressFull,
      bubble: "bg-accent-500/20 text-accent-200",
    },
    ...(clinicInfo.hours.length
      ? [
          {
            icon: "ri-time-line",
            label: "Working Hours",
            value: clinicInfo.hours.join(" · "),
            bubble: "bg-secondary-500/20 text-secondary-200",
          },
        ]
      : []),
  ];

  return (
    <section id="contact" className="scroll-mt-28 bg-gradient-to-b from-background-50 to-primary-50/60 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeading
          eyebrow="Get In Touch"
          title="Visit us in Srirangam, Trichy"
          description="Our team is here to answer your questions and help you find a convenient appointment time."
        />

        <div className="reveal mt-10 overflow-hidden rounded-3xl border border-background-200 lg:grid lg:grid-cols-2">
          {/* Left — deep navy contact information */}
          <div className="relative overflow-hidden bg-primary-950 p-7 md:p-9">
            <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-primary-500/25 blur-3xl" aria-hidden="true"></div>
            <div className="absolute -bottom-16 -left-10 h-56 w-56 rounded-full bg-secondary-500/20 blur-3xl" aria-hidden="true"></div>
            <div className="relative">
              <h3 className="font-heading text-xl font-semibold text-background-50">Clinic details</h3>
              <p className="mt-2 max-w-sm font-body text-sm leading-relaxed text-background-300">
                Reach out by phone, email or visit us in Srirangam — we&rsquo;re glad to help.
              </p>

              <ul className="mt-7 space-y-4">
                {contactItems.map((item) => (
                  <li key={item.label} className="flex items-start gap-3.5">
                    <span className={`flex h-10 w-10 flex-none items-center justify-center rounded-md ${item.bubble}`}>
                      <i className={`${item.icon} text-lg`} aria-hidden="true"></i>
                    </span>
                    <span className="min-w-0">
                      <span className="block font-label text-xs uppercase tracking-wide text-background-300">
                        {item.label}
                      </span>
                      {item.href ? (
                        <a
                          href={item.href}
                          className="mt-0.5 block font-body text-sm font-medium text-background-50 transition-colors hover:text-secondary-300"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <span className="mt-0.5 block font-body text-sm font-medium leading-relaxed text-background-50">
                          {item.value}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Button href={clinicInfo.mapDirections} variant="accent" size="md" icon="ri-navigation-line">
                  Get Directions
                </Button>
                <a
                  href={clinicInfo.phoneHref}
                  className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/20 px-5 py-2.5 font-label text-sm font-semibold text-background-100 transition-colors hover:bg-background-50/10"
                >
                  <i className="ri-phone-fill" aria-hidden="true"></i>
                  Call Now
                </a>
              </div>
            </div>
          </div>

          {/* Right — white contact form */}
          <div className="bg-background-50 p-6 md:p-9">
            <p className="font-label text-xs font-semibold uppercase tracking-[0.16em] text-primary-600">
              Send us a message
            </p>
            <h3 className="mt-2 font-heading text-xl font-semibold text-foreground-950">We&rsquo;ll get back to you</h3>
            <div className="mt-6">
              <ContactForm />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}